/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";
import Media from "react-media";

import { withGettext } from "./common/Gettext";
import { CurrencySymbol, withMoney } from "./Money";
import { tournamentCurrency } from "./Icons/Kuchka";

import { fontSize } from "./common/styled-mixins";

const Wrapper = styled.div`
  display: flex;

  @media (max-width: 479px) {
    width: 200px;
    margin: 0 auto;
  }
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  padding: 6px 18px;
  font-size: 16px;
  line-height: 1;
  font-family: Roboto, Arial, sans-serif;
  color: ${({ theme }) => theme.foreground};
  border: 1px solid ${props => props.theme.borderMainColor};
  border-radius: 4px;
  background: ${props => props.theme.mainDark};
  box-shadow: inset 0px 3px 0px 0px rgba(0, 0, 0, 0.08);
  appearance: none;
  cursor: pointer;

  ${({ theme, invalid }) =>
    invalid &&
    css`
      border-color: ${theme.lower};
      box-shadow: inset 0px 3px 0px 0px rgba(0, 0, 0, 0.08),
        inset 0px 0px 0px 2px ${theme.lower};
    `};
  @media (max-width: 959px) {
    padding: 4px 8px;
  }

  @media (max-width: 479px) {
    display: flex;
    justify-content: center;
    width: 120px;
    min-width: 120px;
    height: 28px;
    background: none;
    border: none;
    border-radius: 0;
    box-shadow: none;

    ${({ theme, invalid }) =>
      invalid &&
      css`
        box-shadow: inset 0px 3px 0px 0px rgba(0, 0, 0, 0.08),
          inset 0px 0px 0px 2px ${theme.lower};
      `};
  }
`;

const ValueContainer = styled.div`
  display: flex;
`;

const NativeSelect = styled.select`
  /* Пока не в фокусе, маскируемся под кнопку */
  position: absolute;
  right: 0;
  width: ${props =>
    props.full || props.isTourMode ? `100%` : `16px`}; /* Это ширина кнопки */
  font-size: 16px;
  line-height: 1;
  font-family: Roboto, Arial, sans-serif;
  opacity: 0;

  :focus {
    left: 0;
    right: 0;
    width: 100%;
    height: 0;
    bottom: 0;
  }
`;

const PretentiousDropdownButton = styled.label`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: flex-end;
  min-height: 16px;
  min-width: 16px;

  @media (min-width: 480px) {
    margin-left: auto;

    :before {
      content: "";
      border: 5px solid transparent;
      border-top-color: #99959d;
      border-bottom: none;
    }
  }
`;

const BorderlessInput = styled.input`
  min-width: 0;
  width: 100%;
  margin: 0;
  padding: 0;
  font-size: 16px;
  line-height: 1;
  font-family: Roboto, Arial, sans-serif;
  color: inherit;
  border: none;
  outline: none;
  appearance: none;
  text-fill-color: ${props =>
    props.disabled ? props.theme.foreground : props.theme.foreground};
  opacity: 1;
  background: transparent;

  @media (min-width: 414px) and (max-width: 959px) {
    font-size: ${fontSize(14)};
  }

  @media (min-width: 414px) and (max-width: 567px) {
    font-size: ${fontSize(12)};
  }

  @media (max-width: 479px) {
    margin-right: 5px;
    width: auto;
    text-align: right;
  }
`;

const Suffix = styled.span`
  flex-grow: 0;
  flex-shrink: 1;
  align-self: center;
  white-space: nowrap;
  font-size: ${fontSize(14)};
  color: #919191;

  @media (max-width: 959px) {
    font-size: ${fontSize(12)};
  }

  @media (min-width: 414px) and (max-width: 567px) {
    font-size: ${fontSize(10)};
  }

  @media (max-width: 479px) {
    color: inherit;
  }
`;

const Button = styled.button`
  position: relative;
  width: 40px;
  min-width: 40px;
  height: 28px;
  background: none;
  border: none;
  border-radius: ${({ direction }) =>
    direction === "plus" ? "0 4px 4px 0" : "4px 0 0 4px"};
  cursor: pointer;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 2px;
    margin-top: -1px;
    margin-left: -6px;
    background-color: ${({ theme }) => theme.suggestingInputButtonSymbol};
  }

  ${({ direction }) =>
    direction === "plus"
      ? css`
          margin-left: 1px;

          &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            width: 2px;
            height: 12px;
            margin-top: -6px;
            margin-left: -1px;
            background-color: ${({ theme }) =>
              theme.suggestingInputButtonSymbol};
          }
        `
      : css`
          margin-right: 1px;
        `};

  ${({ disabled }) =>
    disabled &&
    css`
      background: none;
      cursor: auto;

      &::before,
      &::after {
        background-color: ${({ theme }) =>
          theme.suggestingInputButtonSymbolDisabled};
      }
    `};
`;

type Props = {
  value: string,
  prevValue: any,
  nextValue: any,
  suggestedValues: Array<string>,
  onChange: EventHandler,
  useNativeSelect: boolean,
  gettext: any,
  prefix?: string,
  suffix?: string,
  className?: string,
  style?: Object,
  invalid?: boolean,
  disableInput?: boolean,
  tradeMode?: "binary" | "tournament",
  valueFormatFn?: (value: string) => number,
  isTourMode?: boolean
};

function SuggestingInput(props: Props) {
  // input: HTMLInputElement;
  function determineSuffix(str) {
    return props.valueFormatFn && parseInt(str || 0, 10) < 60
      ? props.gettext`sec`
      : parseInt(str || 0, 10) < 120 || parseInt(str || 0, 10) > 600
      ? props.suffix
      : props.gettext`mins`;
  }

  return (
    <Wrapper>
      <Media
        query="(max-width: 479px)"
        render={() => (
          <Button
            disabled={props.prevValue === null}
            onClick={() =>
              props.onChange({
                target: {
                  value: props.prevValue
                }
              })
            }
          />
        )}
      />
      <Container
        className={props.className}
        style={props.style}
        invalid={props.invalid}
        ref={props.forwardedRef}
      >
        <ValueContainer>
          {props.prefix}
          <BorderlessInput
            size={
              props.valueFormatFn
                ? props.valueFormatFn(props.value).toString().length
                : props.value.toString().length
            }
            value={
              props.valueFormatFn
                ? props.valueFormatFn(props.value)
                : props.value
            }
            onChange={props.onChange}
            type="tel"
            disabled={props.disableInput}
          />
          <Suffix>
            {props.valueFormatFn ? (
              determineSuffix(props.value)
            ) : (
              <CurrencySymbol
                currency={
                  props.tradeMode === "tournament" ? tournamentCurrency : null
                }
              />
            )}
          </Suffix>
        </ValueContainer>
        <PretentiousDropdownButton
          htmlFor={props.valueFormatFn ? "time-select" : "amount-select"}
          full={!!props.valueFormatFn}
        />
        <NativeSelect
          id={props.valueFormatFn ? "time-select" : "amount-select"}
          full={!!props.valueFormatFn}
          value={props.isTourMode ? props.suggestedValues[0] : props.value}
          onChange={props.onChange}
          isTourMode={props.isTourMode}
        >
          {props.suggestedValues.map((value, index) =>
            props.valueFormatFn ? (
              index === 0 && props.isTourMode ? (
                <React.Fragment>
                  <option key="fake" value={value} disabled hidden />
                  <option key={value} value={value}>
                    {props.prefix}
                    {props.valueFormatFn(value)} {determineSuffix(value)}
                  </option>
                </React.Fragment>
              ) : (
                <option key={value} value={value}>
                  {props.prefix}
                  {props.valueFormatFn(value)} {determineSuffix(value)}
                </option>
              )
            ) : index === 0 && props.isTourMode ? (
              <React.Fragment>
                <option key="fake" value={value} disabled hidden />
                <option key={value} value={value}>
                  {props.money(value, {
                    currency:
                      props.tradeMode === "tournament"
                        ? tournamentCurrency
                        : null
                  })}
                </option>
              </React.Fragment>
            ) : (
              <option key={value} value={value}>
                {props.money(value, {
                  currency:
                    props.tradeMode === "tournament" ? tournamentCurrency : null
                })}
              </option>
            )
          )}
        </NativeSelect>
      </Container>
      <Media
        query="(max-width: 479px)"
        render={() => (
          <Button
            disabled={props.nextValue === null}
            direction="plus"
            onClick={() =>
              props.onChange({
                target: {
                  value: props.nextValue
                }
              })
            }
          />
        )}
      />
    </Wrapper>
  );
}

const S = withGettext(withMoney(SuggestingInput));

export default React.forwardRef((props, ref) => (
  <S {...props} forwardedRef={ref} />
));



// WEBPACK FOOTER //
// ./src/components/SuggestingInput.js